import * as CANNON from 'cannon';
import * as THREE from 'three';
import {
    JointConstraints,
    BONE_HALF_EXTENTS,
    BONE_OFFSET,
    BONE_HIERARCHY,
    PROCESS_ORDER,
    RAGDOLL_GROUP,
    LINEAR_DAMPING,
    ANGULAR_DAMPING,
} from './ragdoll';

export class RagdollCollider {
    public readonly bodies: Map<string, CANNON.Body> = new Map();
    public readonly constraints: CANNON.Constraint[] = [];

    private readonly jointConstraints = new JointConstraints();
    private readonly boneMap: Map<string, THREE.Object3D> = new Map();
    private readonly boneSnapshot: Map<string, { position: THREE.Vector3; quaternion: THREE.Quaternion }> = new Map();
    private readonly _tmpPos = new THREE.Vector3();
    private readonly _tmpQuat = new THREE.Quaternion();
    private readonly _tmpOffset = new THREE.Vector3();
    private physicsWorld: CANNON.World | undefined;

    constructor(characterRoot: THREE.Object3D) {
        this.buildBoneMap(characterRoot);
        this.buildBodies();
        this.buildConstraints();
    }

    public syncToBones(): void {
        for (const [boneName, body] of this.bodies) {
            const bone = this.boneMap.get(boneName);
            if (!bone) { continue; }

            bone.getWorldPosition(this._tmpPos);
            bone.getWorldQuaternion(this._tmpQuat);

            const [ox, oy, oz] = BONE_OFFSET[boneName] ?? [0, 0, 0];
            this._tmpOffset.set(ox, oy, oz).applyQuaternion(this._tmpQuat);
            body.position.set(
                this._tmpPos.x + this._tmpOffset.x,
                this._tmpPos.y + this._tmpOffset.y,
                this._tmpPos.z + this._tmpOffset.z,
            );
            body.quaternion.set(this._tmpQuat.x, this._tmpQuat.y, this._tmpQuat.z, this._tmpQuat.w);
            body.velocity.set(0, 0, 0);
            body.angularVelocity.set(0, 0, 0);
        }
    }

    public enablePhysics(initialVelocity?: CANNON.Vec3, excludedBones?: ReadonlySet<string>): void {
        this.boneMap.forEach((bone, name) => {
            const snap = this.boneSnapshot.get(name)!;
            snap.position.copy(bone.position);
            snap.quaternion.copy(bone.quaternion);
        });
        this.rebuildConstraints(excludedBones);

        for (const [boneName, body] of this.bodies) {
            const excluded = excludedBones?.has(boneName) ?? false;
            this.physicsWorld?.remove(body);

            if (excluded) {
                body.type = CANNON.Body.KINEMATIC;
                body.collisionFilterGroup = 1;
                body.collisionFilterMask = 0;
            } else {
                body.type = CANNON.Body.DYNAMIC;
                body.collisionFilterGroup = RAGDOLL_GROUP;
                body.collisionFilterMask = -1;
                if (initialVelocity) { body.velocity.copy(initialVelocity); }
            }

            this.physicsWorld?.addBody(body);
            if (!excluded) { body.wakeUp(); }
        }
    }

    public disablePhysics(): void {
        for (const body of this.bodies.values()) {
            this.physicsWorld?.remove(body);
            body.type = CANNON.Body.KINEMATIC;
            body.collisionFilterGroup = 1;
            body.collisionFilterMask = 0;
            body.velocity.set(0, 0, 0);
            body.angularVelocity.set(0, 0, 0);
            this.physicsWorld?.addBody(body);
        }
        this.boneSnapshot.forEach(({ position, quaternion }, name) => {
            const bone = this.boneMap.get(name);
            if (!bone) { return; }
            bone.position.copy(position);
            bone.quaternion.copy(quaternion);
        });
    }

    public syncBonesFromBodies(): void {
        const tmpBodyQuat = new THREE.Quaternion();
        const tmpParentWorldQuat = new THREE.Quaternion();
        const tmpWorldPos = new THREE.Vector3();

        for (const boneName of PROCESS_ORDER) {
            const body = this.bodies.get(boneName);
            const bone = this.boneMap.get(boneName);
            if (!body || !bone || !bone.parent) { continue; }

            tmpBodyQuat.set(body.quaternion.x, body.quaternion.y, body.quaternion.z, body.quaternion.w);

            const [ox, oy, oz] = BONE_OFFSET[boneName] ?? [0, 0, 0];
            this._tmpOffset.set(ox, oy, oz).applyQuaternion(tmpBodyQuat);
            tmpWorldPos.set(
                body.position.x - this._tmpOffset.x,
                body.position.y - this._tmpOffset.y,
                body.position.z - this._tmpOffset.z,
            );
            bone.parent.worldToLocal(tmpWorldPos);
            bone.position.copy(tmpWorldPos);
            bone.parent.getWorldQuaternion(tmpParentWorldQuat);
            bone.quaternion.copy(tmpParentWorldQuat.inverse().multiply(tmpBodyQuat));

            bone.updateWorldMatrix(false, false);
        }
    }

    public addToWorld(physicsWorld: CANNON.World): void {
        this.physicsWorld = physicsWorld;
        this.bodies.forEach(body => physicsWorld.addBody(body));
        this.constraints.forEach(c => physicsWorld.addConstraint(c));
    }

    public removeFromWorld(physicsWorld: CANNON.World): void {
        this.physicsWorld = undefined;
        this.constraints.forEach(c => physicsWorld.removeConstraint(c));
        this.bodies.forEach(body => physicsWorld.remove(body));
    }

    public clampJointAngle(boneName: string, axis: 'pitch' | 'yaw' | 'roll', angleRad: number): number {
        return this.jointConstraints.clampAngle(boneName, axis, angleRad);
    }

    public validatePose(): Map<string, string[]> {
        const violations = new Map<string, string[]>();
        const tmpQuat  = new THREE.Quaternion();
        const tmpEuler = new THREE.Euler();

        for (const [boneName, bone] of this.boneMap) {
            const limits = this.jointConstraints.getLimitsForBone(boneName);
            if (!limits) { continue; }

            bone.getWorldQuaternion(tmpQuat);
            tmpEuler.setFromQuaternion(tmpQuat, 'XYZ');

            const axes: Array<['pitch' | 'yaw' | 'roll', number]> = [
                ['pitch', tmpEuler.x],
                ['yaw',   tmpEuler.y],
                ['roll',  tmpEuler.z],
            ];
            const boneViolations: string[] = [];

            for (const [axis, angle] of axes) {
                const axisLimits = limits[axis];
                if (!axisLimits) { continue; }
                if (angle < axisLimits.min || angle > axisLimits.max) {
                    const fmt = (v: number) => (v * 180 / Math.PI).toFixed(1);
                    boneViolations.push(
                        `${axis}: ${fmt(angle)}° outside [${fmt(axisLimits.min)}°, ${fmt(axisLimits.max)}°]`,
                    );
                }
            }

            if (boneViolations.length > 0) { violations.set(boneName, boneViolations); }
        }

        return violations;
    }

    // ─── Private ─────────────────────────────────────────────────────────

    private buildBoneMap(root: THREE.Object3D): void {
        const boneNames = new Set(Object.keys(BONE_HALF_EXTENTS));
        root.traverse((obj) => {
            if (boneNames.has(obj.name)) {
                this.boneMap.set(obj.name, obj);
                this.boneSnapshot.set(obj.name, {
                    position: new THREE.Vector3(),
                    quaternion: new THREE.Quaternion(),
                });
            }
        });
    }

    private buildBodies(): void {
        for (const boneName of Object.keys(BONE_HALF_EXTENTS)) {
            const [hx, hy, hz] = BONE_HALF_EXTENTS[boneName];
            const bone = this.boneMap.get(boneName);
            if (!bone) { continue; }

            const worldPos = new THREE.Vector3();
            const worldQuat = new THREE.Quaternion();
            bone.getWorldPosition(worldPos);
            bone.getWorldQuaternion(worldQuat);

            const [ox, oy, oz] = BONE_OFFSET[boneName] ?? [0, 0, 0];
            const offset = new THREE.Vector3(ox, oy, oz).applyQuaternion(worldQuat);
            const body = new CANNON.Body({
                mass: 1,
                type: CANNON.Body.KINEMATIC,
                position: new CANNON.Vec3(worldPos.x + offset.x, worldPos.y + offset.y, worldPos.z + offset.z),
            });
            body.addShape(new CANNON.Sphere(Math.max(hx, hy, hz)));
            body.linearDamping  = LINEAR_DAMPING;
            body.angularDamping = ANGULAR_DAMPING;
            body.collisionFilterMask = 0;

            this.bodies.set(boneName, body);
        }
    }

    private buildConstraints(): void {
        for (const [parentName, childName] of BONE_HIERARCHY) {
            const parentBody = this.bodies.get(parentName);
            const childBody = this.bodies.get(childName);
            if (!parentBody || !childBody) { continue; }

            const { pivotA, pivotB } = this.computePivots(parentBody, childBody, childName);

            const constraint = this.jointConstraints.createConstraint(
                parentBody, childBody,
                pivotA, pivotB,
                new CANNON.Vec3(0, 1, 0),
                new CANNON.Vec3(0, 1, 0),
                childName,
            );
            this.constraints.push(constraint);
        }
    }

    private rebuildConstraints(excludedBones?: ReadonlySet<string>): void {
        if (!this.physicsWorld) { return; }

        this.constraints.forEach(c => this.physicsWorld!.removeConstraint(c));
        this.constraints.length = 0;

        const parentQuatInv = new CANNON.Quaternion();
        const childQuatInv  = new CANNON.Quaternion();
        const worldOffset   = new CANNON.Vec3();
        const pivotA        = new CANNON.Vec3();
        const axisA         = new CANNON.Vec3();
        const axisB         = new CANNON.Vec3();

        for (const [parentName, childName] of BONE_HIERARCHY) {
            const parentBody = this.bodies.get(parentName);
            const childBody  = this.bodies.get(childName);
            if (!parentBody || !childBody) { continue; }

            const parentExcluded = excludedBones?.has(parentName) ?? false;
            const childExcluded  = excludedBones?.has(childName)  ?? false;
            // Skip if child is kinematic: a dynamic parent constrained to kinematic children
            // creates phantom anchor forces that over-constrain nodes like Spine2, which has
            // three kinematic children simultaneously, causing solver oscillation/jitter.
            if (childExcluded) { continue; }

            parentBody.quaternion.inverse(parentQuatInv);
            childBody.quaternion.inverse(childQuatInv);

            const [ox_c, oy_c, oz_c] = BONE_OFFSET[childName] ?? [0, 0, 0];
            const pivotB = new CANNON.Vec3(-ox_c, -oy_c, -oz_c);

            const childOffsetWorld = new CANNON.Vec3();
            childBody.quaternion.vmult(new CANNON.Vec3(ox_c, oy_c, oz_c), childOffsetWorld);

            worldOffset.set(
                childBody.position.x - childOffsetWorld.x - parentBody.position.x,
                childBody.position.y - childOffsetWorld.y - parentBody.position.y,
                childBody.position.z - childOffsetWorld.z - parentBody.position.z,
            );

            parentQuatInv.vmult(worldOffset, pivotA);

            const len = Math.sqrt(worldOffset.x ** 2 + worldOffset.y ** 2 + worldOffset.z ** 2);
            const nx = len > 0 ? worldOffset.x / len : 0;
            const ny = len > 0 ? worldOffset.y / len : 1;
            const nz = len > 0 ? worldOffset.z / len : 0;
            const worldBoneAxis = new CANNON.Vec3(nx, ny, nz);
            parentQuatInv.vmult(worldBoneAxis, axisA);
            childQuatInv.vmult(worldBoneAxis, axisB);

            const constraint = this.jointConstraints.createConstraint(
                parentBody, childBody,
                new CANNON.Vec3(pivotA.x, pivotA.y, pivotA.z),
                pivotB,
                new CANNON.Vec3(axisA.x, axisA.y, axisA.z),
                new CANNON.Vec3(axisB.x, axisB.y, axisB.z),
                childName,
            );
            this.constraints.push(constraint);
        }

        this.constraints.forEach(c => this.physicsWorld!.addConstraint(c));
    }

    private computePivots(
        parentBody: CANNON.Body,
        childBody: CANNON.Body,
        childName: string,
    ): { pivotA: CANNON.Vec3; pivotB: CANNON.Vec3 } {
        const [ox_c, oy_c, oz_c] = BONE_OFFSET[childName] ?? [0, 0, 0];
        const pivotB = new CANNON.Vec3(-ox_c, -oy_c, -oz_c);

        const childOffsetWorld = new CANNON.Vec3();
        (childBody.quaternion as CANNON.Quaternion).vmult(new CANNON.Vec3(ox_c, oy_c, oz_c), childOffsetWorld);

        const toJointWorld = new CANNON.Vec3(
            childBody.position.x - childOffsetWorld.x - parentBody.position.x,
            childBody.position.y - childOffsetWorld.y - parentBody.position.y,
            childBody.position.z - childOffsetWorld.z - parentBody.position.z,
        );

        const parentQuatInv = new CANNON.Quaternion();
        parentBody.quaternion.inverse(parentQuatInv);
        const pivotA = new CANNON.Vec3();
        parentQuatInv.vmult(toJointWorld, pivotA);

        return { pivotA, pivotB };
    }

}
